import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import TournamentList from "../components/game-lobby/TournamentList";
import GameBanner from "../components/game-lobby/GameBanner";
import TournamentConfirmationModal from "../components/game-lobby/TournamentConfirmationModal";
import ResumeGameModal from "../components/game-lobby/ResumeGameModal";
import RulesModal from "../components/game-lobby/RulesModal";
import GameService from "../services/game.service";
import { logClientEvent } from "../services/clientEvents";
import SkeletonTournamentCard from "../components/common/SkeletonTournamentCard";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { enterTournament } from "../store/slice/gamePlay";
import { fetchGameMeta, setGameMeta } from "../store/slice/gamesMeta";
import PromoSpinnerBanner from "../components/common/PromoSpinnerBanner";
import MarqueeBar from "../components/home/MarqueeBar";
import { X } from "lucide-react";
import { USE_MOCK_DATA } from "../common/api_constants";
import {
  mockGameMeta,
  mockTournaments,
  mockMatchResult,
  MOCK_REGISTRATION_ID,
} from "../data/mockGameLobbyData";
import MatchResultSummaryPopUp from "../components/results/MatchResultSummaryPopUp";
import MatchStatsModal from "../components/results/MatchStatsModal";
import { AppImages } from "../common/ImagePath";
import Results from "./Results";
import { useRef } from "react";
import { useLaunch } from "../../auth/useAuth";
import { mapStatus } from "../utils/matchStatus";
import { hasSeenHowToPlay, markHowToPlaySeen } from "../common/howToPlaySeen";
import { resolveDisplayCurrency } from "../common/currencies";
import GameLoader from "../components/common/GameLoader";

// The iframe game reads `authToken` from its URL and uses it to call
// `POST /api/game-finish` on the Game Service. Game Service already injects
// `?room=…&matchId=…`, so we always append with `&`.
const appendAuthToken = (url, token) =>
  url ? `${url}${url.includes("?") ? "&" : "?"}at=${encodeURIComponent(token)}` : "";

// After GAME_ENDED the iframe stays up at least this long (the game's own
// end-screen animation), then until the backend has the score, so closing it
// can't abort an in-flight /game-finish. Capped so a lost score can't hold
// the player in the game forever.
const GAME_ENDED_MIN_MS = 5000;
const FINISH_POLL_MS = 1500;
const FINISH_WAIT_MAX_MS = 30000;

// Operator decline codes for a balance below the entry fee (DK + Pixel).
const INSUFFICIENT_FUNDS_CODES = new Set([
  "INSUFFICIENT_FUNDS",
  "INSUFFICIENT_FUNDS_REAL_MONEY",
  "INSUFFICIENT_BALANCE",
]);

const GameLobby = () => {
  const { gameId } = useParams();
  const dispatch = useDispatch();
  // Wallet operations (debit/credit/rollback) are now driven by
  // rivalz-games-backend via its S2S WalletServiceClient. The React shell
  // never moves money — useLaunch() exposes read state (balance,
  // displayName, currencyCode) plus refreshBalance() to re-read it after the
  // server has moved money (entry debit, payout).
  const {
    currencyCode: sessionCurrency,
    session,
    launchParams,
    isLoading: isSessionLoading,
    refreshBalance,
    navigateToCashier,
  } = useLaunch();
  const playerId = session?.playerId ?? launchParams?.playerId ?? null;
  const [activeTab, setActiveTab] = useState("lobby");
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [gameIframeUrl, setGameIframeUrl] = useState(null);
  const [isMatchNotFoundOpen, setIsMatchNotFoundOpen] = useState(false);
  const [matchErrorMessage, setMatchErrorMessage] = useState("");
  // Operator decline code from POST /game (e.g. INSUFFICIENT_FUNDS) — drives
  // the Deposit action on the "Unable to start match" popup.
  const [matchErrorCode, setMatchErrorCode] = useState(null);
  const [isGameLoading, setIsGameLoading] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  const [showStats, setShowStats] = useState(false);
  // Resume prompt: the in-progress lobby to offer, and a once-per-mount guard so
  // it does not reopen after the user continues or dismisses it.
  const [resumeLobby, setResumeLobby] = useState(null);
  const [resumeHandled, setResumeHandled] = useState(false);
  // How To Play slides: opened from the banner's "?" button, and auto-opened
  // once per player + game on the first lobby visit. The ref records which
  // game the auto-open check has already run for.
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const rulesAutoOpenedForRef = useRef(null);
  const gameEndedRef = useRef(false);
  const playingTournamentRef = useRef(null);
  // Tracks whether the game iframe was open, so we can refresh the lobby list
  // once on every open -> closed transition (resume state may have changed).
  const gameWasOpenRef = useRef(false);
  // Loader-dismissal timers for the iframe boot overlay. onLoad fires a short
  // grace timer; a hard cap guarantees the overlay never sticks even if the
  // game never loads or never emits GAME_STARTED/GAME_READY/GAME_LOADED.
  const loaderGraceRef = useRef(null);
  const loaderCapRef = useRef(null);
  // A GET /lobby is in flight (or about to be): the resume check must not run
  // against the previous response. The ref gates synchronously (effects in
  // the same flush), the state re-runs the check once the refresh lands.
  const [lobbyRefreshPending, setLobbyRefreshPending] = useState(false);
  const lobbyRefreshPendingRef = useRef(false);
  const lobbyRequestSeqRef = useRef(0);
  // GAME_ENDED → wait-for-score → close. The token invalidates a poll that is
  // still in flight when the iframe closes another way or the page unmounts.
  const finishWaitTimerRef = useRef(null);
  const finishWaitTokenRef = useRef(0);
  const gameEndedAtRef = useRef(null);
  // Why the iframe is being closed, for the iframe_closed diagnostic.
  const closeReasonRef = useRef(null);
  const gameIframeOpenRef = useRef(false);

  // Diagnostics only (see services/clientEvents.js); never affects behaviour.
  const logEvent = useCallback(
    (event, fields = {}) =>
      logClientEvent(event, {
        sessionId: session?.sessionId,
        gameName: gameId,
        registrationId: localStorage.getItem("currentRegistrationId"),
        ...fields,
      }),
    [session?.sessionId, gameId],
  );

  const clearFinishWait = useCallback(() => {
    if (finishWaitTimerRef.current) clearTimeout(finishWaitTimerRef.current);
    finishWaitTimerRef.current = null;
    finishWaitTokenRef.current += 1;
  }, []);

  const clearLoaderTimers = useCallback(() => {
    if (loaderGraceRef.current) clearTimeout(loaderGraceRef.current);
    if (loaderCapRef.current) clearTimeout(loaderCapRef.current);
    loaderGraceRef.current = null;
    loaderCapRef.current = null;
  }, []);

  const hideGameLoader = useCallback(() => {
    clearLoaderTimers();
    setIsGameLoading(false);
  }, [clearLoaderTimers]);
  const status = useSelector((state) => state.gamesMeta.status);
  const reduxGameMetaData = useSelector((state) => state.gamesMeta.data);

  const gameMetaData = useMemo(
    () => (USE_MOCK_DATA ? mockGameMeta(gameId) : reduxGameMetaData),
    [reduxGameMetaData, gameId],
  );

  const dynamicLayout = useMemo(() => {
    return gameMetaData?.lobbyMeta?.[gameId]?.LAYOUT?.sections || [];
  }, [gameMetaData, gameId]);

  const fetchMatchResult = async (registrationId) => {
    if (USE_MOCK_DATA) {
      await new Promise((r) => setTimeout(r, 1000));
      return mockMatchResult;
    }

    const json =
      await GameService.getMatchResultByRegistrationId(registrationId);

    const match = json?.data?.match;

    if (!match) throw new Error("Match not ready");

    const { status, result } = mapStatus(match);

    return {
      amount: Number(match.amountWon?.value || 0),
      userName: match.player?.username || "You",
      userScore: match.score ?? null,
      opponentName: match.opponent?.username || null,
      opponentScore: match.opponentScore ?? null,
      status,
      result,
      isPending: match.gameResult === "MATCH_IN_PROGRESS",
      // Fall back to the lobby's currency so a demo-gems match without an
      // amountWon still hides its amounts.
      currencyCode:
        match.amountWon?.currencyCode ||
        playingTournamentRef.current?.currencyCode ||
        "USD",
      breakdown: match.breakdown ?? null,
      opponentBreakdown: match.opponentBreakdown ?? null,
    };
  };

  useEffect(() => {
    if (gameIframeUrl) {
      document.body.style.overflow = "hidden";
      // Hard cap: no matter what the game does (or doesn't) signal, never let
      // the boot overlay stick for more than this. Reveals whatever the iframe
      // is showing so the player isn't stuck on a black/loading screen.
      clearLoaderTimers();
      loaderCapRef.current = setTimeout(hideGameLoader, 15000);
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [gameIframeUrl, clearLoaderTimers, hideGameLoader]);

  const closeGame = useCallback(() => {
    clearLoaderTimers();
    clearFinishWait();
    if (gameIframeOpenRef.current) {
      logEvent("iframe_closed", {
        reason: closeReasonRef.current || "unknown",
        msSinceGameEnded: gameEndedAtRef.current
          ? Date.now() - gameEndedAtRef.current
          : null,
      });
      // The open → closed effect is about to refresh the lobby list; block
      // the resume check until it lands so it can't read the pre-game
      // snapshot. Only when an iframe is really closing — otherwise no
      // refresh follows to clear the flag.
      lobbyRefreshPendingRef.current = true;
    }
    closeReasonRef.current = null;
    setGameIframeUrl(null);
    setIsGameLoading(false);
    setSelectedTournament(null);
    setIsConfirmationModalOpen(false);
    setIsMatchNotFoundOpen(false);
    document.body.style.overflow = "auto";
  }, [clearLoaderTimers, clearFinishWait, logEvent]);

  // Fetch the lobby list.
  // A newer call supersedes one still in flight; only the latest clears the
  // pending flag, so the resume check always waits for the freshest response.
  const refreshLobbies = useCallback(async () => {
    if (USE_MOCK_DATA) {
      setTournament(mockTournaments(gameId));
      setLoading(false);
      return;
    }
    const seq = ++lobbyRequestSeqRef.current;
    const startedAt = Date.now();
    lobbyRefreshPendingRef.current = true;
    setLobbyRefreshPending(true);
    try {
      const response = await GameService.getGameById({
        gameName: gameId,
        page: 1,
        limit: 10,
      });
      if (seq !== lobbyRequestSeqRef.current) return;
      setTournament(response?.data);
      const resumable = (response?.data?.lobbies || []).find(
        (lobby) => lobby.isResumable,
      );
      logEvent("lobby_refreshed", {
        resumableLobbyId: resumable?._id ?? null,
        gameWindowEndsAt: resumable?.userActiveSession?.gameWindowEndsAt ?? null,
        durationMs: Date.now() - startedAt,
      });
    } catch (error) {
      console.error("Error fetching game data:", error);
    } finally {
      if (seq === lobbyRequestSeqRef.current) {
        lobbyRefreshPendingRef.current = false;
        setLobbyRefreshPending(false);
        setLoading(false);
      }
    }
  }, [gameId, logEvent]);

  // Refresh the lobby list whenever the game iframe closes (game ended, quit,
  // or any other close path), so the per-lobby `isResumable` flag reflects the
  // now-current session state instead of the stale mount-time snapshot.
  useEffect(() => {
    gameIframeOpenRef.current = !!gameIframeUrl;
    if (gameIframeUrl) {
      gameWasOpenRef.current = true;
    } else if (gameWasOpenRef.current) {
      gameWasOpenRef.current = false;
      refreshLobbies();
    }
  }, [gameIframeUrl, refreshLobbies]);

  useEffect(() => {
    const handleMessage = async (event) => {
      let data = event.data;

      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }

      if (data?.eventName) {
        logEvent("game_message", { eventName: String(data.eventName) });
      }

      // Not every game emits GAME_STARTED/GAME_READY/GAME_LOADED, so these are only the
      // *fast path* for dismissing the boot loader — the iframe onLoad + hard
      // cap below guarantee it hides regardless.
      if (data?.eventName === "GAME_STARTED" || data?.eventName === "GAME_READY" || data?.eventName === "GAME_LOADED") {
        // Entry-fee debit is handled server-side by matchmaking.service in
        // rivalz-games-backend at registration time. No client wallet call
        // needed here — just hide the loader.
        hideGameLoader();
      }

      if (data?.eventName === "GAME_ENDED") {
        if (gameEndedRef.current) return;
        gameEndedRef.current = true;
        gameEndedAtRef.current = Date.now();
        clearFinishWait();
        const token = finishWaitTokenRef.current;

        const finishAndClose = (outcome) => {
          if (token !== finishWaitTokenRef.current) return;
          logEvent("finish_wait", {
            ...outcome,
            waitedMs: Date.now() - gameEndedAtRef.current,
          });
          closeReasonRef.current = "game_ended";
          closeGame();
          setMatchResult({
            isPending: true,
            status: "pending",
            currencyCode: playingTournamentRef.current?.currencyCode || "USD",
          });
        };

        // The match endpoint answers 409 while the session is live and
        // resolves once the score is recorded, so "resolved" = the
        // /game-finish landed and the iframe can go.
        const pollForScore = () => {
          if (token !== finishWaitTokenRef.current) return;
          const registrationId = localStorage.getItem("currentRegistrationId");
          if (USE_MOCK_DATA || !registrationId) {
            finishAndClose({ scoreSeen: false, timedOut: false, skipped: true });
            return;
          }
          if (Date.now() - gameEndedAtRef.current >= FINISH_WAIT_MAX_MS) {
            finishAndClose({ scoreSeen: false, timedOut: true });
            return;
          }
          fetchMatchResult(registrationId)
            .then(() => true)
            .catch(() => false)
            .then((recorded) => {
              if (recorded) {
                finishAndClose({ scoreSeen: true, timedOut: false });
              } else if (token === finishWaitTokenRef.current) {
                finishWaitTimerRef.current = setTimeout(
                  pollForScore,
                  FINISH_POLL_MS,
                );
              }
            });
        };

        // Let the game play its own end-screen animation first.
        finishWaitTimerRef.current = setTimeout(pollForScore, GAME_ENDED_MIN_MS);
      }
      // 🟡 Optional: handle GAME_CLOSE
      if (data?.eventName === "GAME_CLOSE") {
        // ignore GAME_CLOSE after GAME_ENDED
        if (gameEndedRef.current) return;
        closeReasonRef.current = "game_close";
        closeGame();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      clearLoaderTimers();
    };
  }, [closeGame, hideGameLoader, clearLoaderTimers, clearFinishWait, logEvent]);

  // A pending wait-for-score must not outlive the page. Kept out of the
  // message effect, which re-subscribes whenever its callbacks change.
  useEffect(() => clearFinishWait, [clearFinishWait]);

  // Covers the host page closing the iframe (Pixel's X), the tab or browser
  // closing, and the app being backgrounded — none of which the game reports.
  useEffect(() => {
    const onHidden = (event) => {
      if (event.type === "visibilitychange" && document.visibilityState !== "hidden") {
        return;
      }
      logEvent("page_hidden", {
        trigger: event.type,
        iframeOpen: gameIframeOpenRef.current,
        gameEnded: gameEndedRef.current,
      });
    };
    window.addEventListener("pagehide", onHidden);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      window.removeEventListener("pagehide", onHidden);
      document.removeEventListener("visibilitychange", onHidden);
    };
  }, [logEvent]);

  useEffect(() => {
    if (!matchResult?.isPending) return;
    const registrationId = localStorage.getItem("currentRegistrationId");
    if (!registrationId) return;

    let cancelled = false;
    const poll = () => {
      fetchMatchResult(registrationId)
        .then((apiResult) => {
          if (!cancelled) setMatchResult(apiResult);
        })
        .catch(() => {
          // backend not ready yet — next tick will retry
        });
    };

    poll();
    const id = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [matchResult?.isPending]);

  // Settlement (prize credit / refund) has run once the result is final.
  const isMatchResultFinal = !!matchResult && !matchResult.isPending;
  useEffect(() => {
    if (isMatchResultFinal) refreshBalance();
  }, [isMatchResultFinal, refreshBalance]);

  useEffect(() => {
    if (gameId) refreshLobbies();
  }, [gameId, refreshLobbies]);

  useEffect(() => {
    if (USE_MOCK_DATA) {
      dispatch(setGameMeta(mockGameMeta(gameId)));
      return;
    }
    if (status === "idle") {
      dispatch(fetchGameMeta());
    }
  }, [status, dispatch, gameId]);

  // On Pixel, show only lobbies whose display currency matches the session
  // currency (USD lobby -> RCR, GEMS lobby -> GEM). Other clients (DraftKings)
  // are unfiltered — they show both their cash and free-to-play sections.
  const visibleLobbies = useMemo(() => {
    const lobbies = tournament?.lobbies || [];
    if (process.env.REACT_APP_CLIENT !== "pixel" || !sessionCurrency) return lobbies;
    return lobbies.filter(
      (data) => resolveDisplayCurrency(data.currencyCode) === sessionCurrency,
    );
  }, [tournament, sessionCurrency]);

  // A lobby (already scoped to the chosen currency) where the user has an
  // in-progress game the backend will resume. `isResumable` from GET /lobby
  // already encodes: game is resumable + active registration + window open.
  const resumableLobby = useMemo(
    () => visibleLobbies.find((data) => data.isResumable) ?? null,
    [visibleLobbies],
  );

  // Offer the resume prompt once, when a resumable lobby is present and nothing
  // else is on screen — and only from a lobby list fetched after the last game
  // closed, never from the snapshot taken before it.
  useEffect(() => {
    if (
      resumeHandled ||
      !resumableLobby ||
      gameIframeUrl ||
      isConfirmationModalOpen ||
      matchResult ||
      lobbyRefreshPending ||
      lobbyRefreshPendingRef.current
    ) {
      return;
    }
    setResumeLobby(resumableLobby);
  }, [
    resumeHandled,
    resumableLobby,
    gameIframeUrl,
    isConfirmationModalOpen,
    matchResult,
    lobbyRefreshPending,
  ]);

  // Close the prompt as soon as fresh lobby data stops flagging that session
  // (score landed, window closed). Doesn't mark it handled, so a session that
  // is still genuinely resumable can be offered again.
  useEffect(() => {
    if (!resumeLobby || isGameLoading || lobbyRefreshPending) return;
    if (resumableLobby?._id === resumeLobby._id) return;
    logEvent("resume_prompt_closed_stale", { lobbyId: resumeLobby._id });
    setResumeLobby(null);
  }, [resumeLobby, resumableLobby, isGameLoading, lobbyRefreshPending, logEvent]);

  const resumeLobbyId = resumeLobby?._id ?? null;
  useEffect(() => {
    if (!resumeLobbyId) return;
    logEvent("resume_prompt_shown", {
      lobbyId: resumeLobbyId,
      gameWindowEndsAt: resumeLobby?.userActiveSession?.gameWindowEndsAt ?? null,
    });
    // Log once per prompt, not on every lobby refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeLobbyId]);

  // Auto-open How To Play the first time this player visits this game's lobby.
  // Waits for the session (so the seen-flag is keyed to the real playerId), the
  // slides and the lobby list, and never stacks on another popup. A pending
  // resume prompt goes first (checked via `resumableLobby`, since `resumeLobby`
  // is only set by an effect a tick later); once the player enters any game this
  // visit — including via Continue — the auto-open is skipped.
  const hasRulesSlides =
    (gameMetaData?.lobbyMeta?.[gameId]?.howToPlay?.slides?.length ?? 0) > 0;
  useEffect(() => {
    if (
      rulesAutoOpenedForRef.current === gameId ||
      isSessionLoading ||
      loading ||
      !hasRulesSlides ||
      (resumableLobby && !resumeHandled) ||
      lobbyRefreshPending ||
      resumeLobby ||
      gameIframeUrl ||
      isGameLoading ||
      isConfirmationModalOpen ||
      isMatchNotFoundOpen ||
      matchResult
    ) {
      return;
    }
    rulesAutoOpenedForRef.current = gameId;
    if (!hasSeenHowToPlay(playerId, gameId)) setIsRulesOpen(true);
  }, [
    gameId,
    playerId,
    isSessionLoading,
    loading,
    hasRulesSlides,
    resumableLobby,
    resumeHandled,
    resumeLobby,
    gameIframeUrl,
    isGameLoading,
    isConfirmationModalOpen,
    isMatchNotFoundOpen,
    matchResult,
    lobbyRefreshPending,
  ]);

  const handleRulesClose = () => {
    setIsRulesOpen(false);
    markHowToPlaySeen(playerId, gameId);
  };

  // Close the resume prompt once the game launches or an error takes over; the
  // prompt itself stays up (showing its loader) during the in-between.
  useEffect(() => {
    if (gameIframeUrl || isMatchNotFoundOpen) setResumeLobby(null);
  }, [gameIframeUrl, isMatchNotFoundOpen]);

  const cashGamesTournaments = visibleLobbies.filter(
    (data) => data.lobbyFormat === "h2h" && data.currencyCode === "USD",
  );
  const freeToPlayTournaments = visibleLobbies.filter(
    (data) => data.currencyCode === "GEMS",
  );
  const showSpinnerPromo =
    gameMetaData?.lobbyMeta?.[gameId]?.showDailySpinWheel;
  const featuredTournaments = visibleLobbies.filter(
    (data) => data.lobbyFormat === "tournament" && data.currencyCode === "USD",
  );
  const handlePlayClick = (tournament) => {
    setSelectedTournament(tournament);
    setIsConfirmationModalOpen(true);
  };

  const handleConfirmEntry = async (tournamentOverride, { source } = {}) => {
    const tournamentToUse = tournamentOverride?._id
      ? tournamentOverride
      : selectedTournament;
    if (!tournamentToUse) return;
    playingTournamentRef.current = tournamentToUse;
    gameEndedAtRef.current = null;
    rulesAutoOpenedForRef.current = gameId;
    setIsGameLoading(true);

    if (USE_MOCK_DATA) {
      localStorage.setItem("currentRegistrationId", MOCK_REGISTRATION_ID);
      setTimeout(() => {
        setGameIframeUrl(gameMetaData?.lobbyMeta?.[gameId]?.gameUrl);
        setIsConfirmationModalOpen(false);
        setMatchResult(null);
      }, 2000);
      return;
    }

    try {
      const result = await dispatch(
        enterTournament({
          gameName: tournamentToUse?.gameName,
          lobbyId: tournamentToUse?._id,
        }),
      ).unwrap();

      const game = result?.data?.game;
      const registrationId = game?.registrationId;

      if (!registrationId) {
        throw new Error("POST /game did not return registrationId");
      }
      if (source === "resume") {
        // Same id = the backend resumed the session; a new id = it found
        // nothing active and started (and charged) a fresh entry.
        const previousRegistrationId = localStorage.getItem("currentRegistrationId");
        logEvent("resume_continue", {
          lobbyId: tournamentToUse?._id,
          registrationId,
          previousRegistrationId,
          sameSession: previousRegistrationId === registrationId,
        });
      }
      localStorage.setItem("currentRegistrationId", registrationId);
      // The entry fee was debited server-side during registration.
      refreshBalance();

      // Prefer the per-session webviewUrl + authToken returned by Game Service
      // (via POST /game). Falls back to the static `meta.gameUrl` for envs
      // where Game Service isn't wired yet.
      const webviewUrl = game?.webviewUrl;
      const authToken = game?.authToken;
      const fallbackUrl = gameMetaData?.lobbyMeta?.[gameId]?.gameUrl;
      const iframeUrl =
        webviewUrl && authToken
          ? appendAuthToken(webviewUrl, authToken)
          : fallbackUrl;

      if (!iframeUrl) {
        console.warn(
          `[migration] No webviewUrl from POST /game and no meta.gameUrl fallback for gameId=${gameId}`,
        );
        setIsGameLoading(false);
        setIsConfirmationModalOpen(false);
        setMatchErrorMessage("This game is not configured.");
        setMatchErrorCode(null);
        setIsMatchNotFoundOpen(true);
        setMatchResult(null);
        return;
      }

      setTimeout(() => {
        setGameIframeUrl(iframeUrl);
        setIsConfirmationModalOpen(false);
        setMatchResult(null);
      }, 2000);
    } catch (error) {
      setIsGameLoading(false);
      setIsConfirmationModalOpen(false);
      setMatchErrorMessage(
        typeof error === "string" ? error : (error?.message ?? ""),
      );
      setMatchErrorCode(error?.code ?? null);
      setIsMatchNotFoundOpen(true);
      setMatchResult(null);
      console.error("Error entering tournament:", error);
    }
  };

  const closeMatchError = () => {
    setIsMatchNotFoundOpen(false);
    setMatchErrorMessage("");
    setMatchErrorCode(null);
  };
  // Deposit is offered only when the operator declined for funds and the
  // launch gave us a cashier to send the player to.
  const canDeposit =
    INSUFFICIENT_FUNDS_CODES.has(matchErrorCode) && !!launchParams?.cashierUrl;

  const handlePlayAgain = () => {
    const tournament = playingTournamentRef.current;
    if (!tournament) return;
    gameEndedRef.current = false;
    setSelectedTournament(tournament);
    handleConfirmEntry(tournament);
  };

  // Continue re-launches the SAME session: POST /game resumes and returns the
  // same matchId + auth token, with no wallet debit (backend returns before
  // charging). The prompt stays up showing its loader until the iframe opens.
  const handleResumeContinue = () => {
    const lobby = resumeLobby;
    if (!lobby) return;
    setResumeHandled(true);
    handleConfirmEntry(lobby, { source: "resume" });
  };

  const handleResumeDismiss = () => {
    logEvent("resume_dismissed", { lobbyId: resumeLobby?._id });
    setResumeHandled(true);
    setResumeLobby(null);
  };

  const renderSection = (section, index) => {
    if (loading)
      return [1, 2, 3, 4, 5].map((item) => (
        <SkeletonTournamentCard key={`${section.type}-${index}`} />
      ));

    switch (section.type) {
      case "tournaments":
        return (
          <TournamentList
            key="tournaments"
            title="Featured Tournaments"
            tournaments={featuredTournaments}
            onPlayNow={handlePlayClick}
            variant="featured"
          />
        );
      case "cash_games":
        return (
          <TournamentList
            key="cash_games"
            title="H2H Games"
            tournaments={cashGamesTournaments}
            onPlayNow={handlePlayClick}
            variant="cash"
          />
        );
      case "free_games":
        return (
          <TournamentList
            key="free_games"
            title={process.env.REACT_APP_CLIENT !== "pixel" ? "Free to Play" : "H2H Games"}
            tournaments={freeToPlayTournaments}
            onPlayNow={handlePlayClick}
            variant="practice"
            spinnerPromoShown={showSpinnerPromo}
          />
        );

      case "daily_spin_wheel":
        return (
          <div className="mb-3">
            <PromoSpinnerBanner
              leftImage={"/assets/images/games/leftimage.png"}
              rightImage={"/assets/images/games/rightimage.png"}
              title="WIN FREE TICKETS DAILY"
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen pb-20">
      <div className="max-w-2xl mx-auto pb-4 sm:pb-6 text-foreground">
        <GameBanner gameName={gameId} onRulesClick={() => setIsRulesOpen(true)} />
        {tournament?.winner_ticker?.length > 0 && (
          <MarqueeBar data={tournament.winner_ticker} />
        )}

        {/* Lobby / Results tab bar */}
        <div className="flex items-stretch border-b border-white/10 mt-2">
          {[
            { key: "lobby", label: "Lobby" },
            { key: "results", label: "Results" },
          ].map(({ key, label }) => {
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`flex-1 py-3 text-center text-sm font-medium capitalize transition-colors duration-150 whitespace-nowrap relative
                  ${isActive ? "text-white" : "text-white/60 hover:text-white"}`}
              >
                {label}
                {isActive && (
                  <span
                    className="absolute bottom-0 left-0 w-full h-0.5 rounded-full bg-[#53d337]"
                    aria-hidden
                  />
                )}
              </button>
            );
          })}
        </div>

        {activeTab === "lobby" ? (
          <div className="mt-6">
            {dynamicLayout.length > 0 &&
              dynamicLayout.map((section, index) =>
                renderSection(section, index),
              )}
          </div>
        ) : (
          <Results gameName={gameId} />
        )}
      </div>

      <TournamentConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={handleConfirmEntry}
        isConfirming={isGameLoading}
        tournament={selectedTournament}
        paymentMethod={
          selectedTournament?.currencyCode === "GEMS" ? "tickets" : "coins"
        }
        displayName={gameMetaData?.lobbyMeta?.[gameId]?.displayName || ""}
      />

      <RulesModal
        isOpen={isRulesOpen}
        onClose={handleRulesClose}
        gameName={gameId}
        gameMetaData={gameMetaData}
      />

      <ResumeGameModal
        isOpen={!!resumeLobby}
        lobby={resumeLobby}
        isLoading={isGameLoading}
        onContinue={handleResumeContinue}
        onDismiss={handleResumeDismiss}
      />

      {isMatchNotFoundOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={closeMatchError}
          role="dialog"
          aria-modal="true"
          aria-labelledby="match-not-found-title"
        >
          <div
            className="w-full max-w-[320px] bg-blackShade2 rounded-2xl shadow-xl p-6 relative border border-white/5"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeMatchError}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-white/70 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <h2
              id="match-not-found-title"
              className="text-lg font-semibold text-white pr-8 mb-2"
            >
              Unable to start match
            </h2>
            <p className="text-sm text-lightGray mb-6">
              {matchErrorMessage || "Please try again later."}
            </p>
            {canDeposit ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    closeMatchError();
                    navigateToCashier();
                  }}
                  className="w-full py-3 rounded-xl bg-brand text-black font-semibold hover:opacity-90 transition"
                >
                  Deposit
                </button>
                <button
                  type="button"
                  onClick={closeMatchError}
                  className="w-full mt-3 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
                >
                  Not now
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={closeMatchError}
                className="w-full py-3 rounded-xl bg-brand text-black font-semibold hover:opacity-90 transition"
              >
                OK
              </button>
            )}
          </div>
        </div>
      )}

      {gameIframeUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center overflow-y-auto">
          <div className="relative w-full h-full bg-gray-900 overflow-hidden">
            {/* <button
              onClick={() => setGameIframeUrl(null)}
              className="absolute top-8 right-4 sm:top-2 sm:right-2 bg-black text-white rounded-full w-8 h-8 flex items-center justify-center z-10 hover:bg-white/10"
              aria-label="Close game"
            >
              <X />
            </button> */}
            <iframe
              src={gameIframeUrl}
              className="w-full h-full border-0"
              allowFullScreen
              title="Game"
              allow="autoplay; fullscreen; microphone; camera; gamepad; payment"
              onLoad={() => {
                // The iframe document has loaded — universal signal that every
                // game fires, unlike GAME_STARTED/GAME_READY/GAME_LOADED. Give the game a
                // short grace period to paint its own loader, then reveal it.
                if (loaderGraceRef.current) clearTimeout(loaderGraceRef.current);
                loaderGraceRef.current = setTimeout(hideGameLoader, 1200);
              }}
            />
            {isGameLoading && (
              <div
                className="absolute inset-0 z-10 flex items-center justify-center bg-gray-900"
                role="status"
                aria-label="Loading game"
              >
                <GameLoader />
              </div>
            )}
          </div>
        </div>
      )}
      {matchResult && (
        <MatchResultSummaryPopUp
          amount={matchResult.amount}
          userName={matchResult.userName}
          userScore={matchResult.userScore}
          opponentName={matchResult.opponentName}
          opponentScore={matchResult.opponentScore}
          status={matchResult.status}
          result={matchResult.result}
          sunburstImage={AppImages.radialPattern}
          isPending={matchResult.isPending}
          breakdown={matchResult.breakdown}
          onMatchStats={() => setShowStats(true)}
          onClose={() => {
            gameEndedRef.current = false;
            gameEndedAtRef.current = null;
            // Block the resume check until the refresh below lands; the
            // current list may predate the score.
            lobbyRefreshPendingRef.current = true;
            setMatchResult(null);
            setShowStats(false);
            // Ensure the lobby reflects the now-completed match (covers the
            // case where the score landed after the game-end refresh).
            refreshLobbies();
            // Catch an operator credit that landed after the result appeared.
            refreshBalance();
          }}
          onPlayAgain={handlePlayAgain}
          isPlayAgainLoading={isGameLoading}
          entryFee={playingTournamentRef.current?.entryFee ?? 0}
          currencyCode={matchResult.currencyCode}
        />
      )}
      {matchResult && showStats && (
        <MatchStatsModal
          gameName={gameId}
          breakdown={matchResult.breakdown}
          opponentBreakdown={matchResult.opponentBreakdown}
          userName={matchResult.userName}
          opponentName={matchResult.opponentName}
          userScore={matchResult.userScore}
          opponentScore={matchResult.opponentScore}
          status={matchResult.status}
          onBack={() => setShowStats(false)}
        />
      )}
    </div>
  );
};

export default GameLobby;
